import RecommendationListItem, { RecommendationListItemProps } from "./RecommendationListItem";
import styles from "./RecommendationList.module.css";
import { useCallback, useEffect, useRef, useState, WheelEvent } from "react";
import Icon from "../common/Icon";

interface RecommendationListProps {
    items: RecommendationListItemProps[];
}
/**
 * @author cjs
 * @date 2024-08-20 17:34:11
 * @remark 공개 지도 추천 목록
 * @param props RecommendationListProps
 * @props items (required) : 추천 지도 목록
 * @returns <div>
 */
function RecommendationList({ items }: RecommendationListProps) {
    const scrollRef = useRef<HTMLDivElement>(null);
    // const wheelEvent = (event: WheelEvent<HTMLDivElement>) => {
    //     event.stopPropagation();
    //     if (!scrollRef.current) return;
    //     scrollRef.current.scrollLeft += event.deltaY;
    // };

    // 화면이 줄어들었을 때 화살표 추가 | 250821 | smyun

    // ===== 옵션: 스크롤 감도 조절 =====
    const MULTIPLIER = 3.2; // 휠 델타 가속
    const PAGE_RATIO = 0.85; // 페이지 스크롤 시 이동 비율(보이는 영역의 85%)
    const SNAP_THRESHOLD = 20; // 이 값 이하의 작은 델타는 페이지 스크롤로 취급

    // ----- 화살표 표시 여부 관리 -----
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(false);

    // 현재 스크롤 위치 기반으로 화살표 상태 갱신
    const updateArrows = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        setCanPrev(el.scrollLeft > 0);
        setCanNext(el.scrollLeft < max - 1);
    }, []);

    // 초기/리사이즈/스크롤 시 갱신
    useEffect(() => {
        updateArrows();
        const onResize = () => updateArrows();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [updateArrows]);

    const onScroll = () => updateArrows();

    // ----- 공통 스크롤 유틸 -----
    const pageScroll = useCallback((dir: "prev" | "next") => {
        const el = scrollRef.current;
        if (!el) return;
        const distance = Math.round(el.clientWidth * PAGE_RATIO) * (dir === "next" ? 1 : -1);
        el.scrollBy({ left: distance, behavior: "smooth" });
    }, []);

    const prev = () => pageScroll("prev");
    const next = () => pageScroll("next");

    // ----- wheel: 한 번에 "쭉" 움직이게 -----
    const wheelHandler = useCallback(
        (e: WheelEvent) => {
            const el = scrollRef.current;
            if (!el) return;

            // 기본(세로) 스크롤 방지
            e.preventDefault();

            // deltaX(트랙패드 가로) + deltaY(세로) 합산 → 가로로 처리
            const raw = (e.deltaX || 0) + (e.deltaY || 0);

            if (Math.abs(raw) <= SNAP_THRESHOLD) {
                // 아주 작은 제스처는 페이지 단위로 이동(휠 한 칸에 시원하게)
                pageScroll(raw >= 0 ? "next" : "prev");
            } else {
                // 그 외엔 가속 적용
                el.scrollLeft += raw * MULTIPLIER;
            }

            updateArrows();
        },
        [pageScroll, updateArrows]
    );

    // non-passive wheel listener (React onWheel 제거)
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener("wheel", wheelHandler as any, { passive: false });
        return () => el.removeEventListener("wheel", wheelHandler as any);
    }, [wheelHandler]);

    return (
        // <div className={styles.wrap} onWheel={wheelEvent} ref={scrollRef}>
        //     {items.map((item, idx) => {
        //         return <RecommendationListItem {...item} key={idx} />;
        //     })}
        // </div>

        <div className={styles.wrap}>
            {/* 화살표_좌*/}
            {canPrev && (
                <button className={`${styles.nav} ${styles.left}`} onClick={prev} aria-label="이전 추천">
                    <Icon name="arrowBack" color="white" />
                </button>
            )}

            <div className={styles.scroller} onScroll={onScroll} ref={scrollRef}>
                {items.map((item, idx) => (
                    <RecommendationListItem {...item} key={idx} />
                ))}
            </div>

            {/* 화살표_우*/}
            {canNext && (
                <button className={`${styles.nav} ${styles.right}`} onClick={next} aria-label="다음 추천">
                    <Icon name="arrowForward" color="white" />
                </button>
            )}
        </div>
    );
}

export default RecommendationList;
